import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play, Brain, RotateCcw } from "lucide-react";

// Pattern / sequence IQ test. 10 questions, timed. Score -> IQ-style result.
const QUESTIONS = [
  { q: "2, 4, 6, 8, ?", options: [9, 10, 11, 12], answer: 10 },
  { q: "1, 4, 9, 16, ?", options: [20, 24, 25, 36], answer: 25 },
  { q: "3, 6, 12, 24, ?", options: [36, 48, 30, 42], answer: 48 },
  { q: "1, 1, 2, 3, 5, ?", options: [7, 8, 6, 9], answer: 8 },
  { q: "2, 3, 5, 7, 11, ?", options: [12, 13, 14, 15], answer: 13 },
  { q: "100, 50, 25, ?", options: [10, 12.5, 15, 20], answer: 12.5 },
  { q: "5, 10, 20, 40, ?", options: [60, 70, 80, 100], answer: 80 },
  { q: "81, 27, 9, ?", options: [3, 6, 1, 0], answer: 3 },
  { q: "1, 8, 27, 64, ?", options: [100, 121, 125, 144], answer: 125 },
  { q: "7, 14, 28, 56, ?", options: [98, 112, 102, 84], answer: 112 },
  { q: "2, 6, 12, 20, 30, ?", options: [40, 42, 44, 36], answer: 42 },
  { q: "1, 3, 6, 10, 15, ?", options: [18, 20, 21, 24], answer: 21 },
];

function pickQuestions() {
  return [...QUESTIONS].sort(() => Math.random() - 0.5).slice(0, 10);
}

function iqFor(correct) {
  // 0->85, 10->145, smooth-ish
  return Math.round(85 + correct * 6);
}

function label(iq) {
  if (iq >= 130) return "Genius level 🧠";
  if (iq >= 115) return "Sharp & gifted";
  if (iq >= 100) return "Above average";
  if (iq >= 90) return "Solid thinker";
  return "Keep training!";
}

export function IQTest({ onScore }) {
  const [questions, setQuestions] = useState(pickQuestions);
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [time, setTime] = useState(0);
  const [picked, setPicked] = useState(null);
  const reported = useRef(false);

  useEffect(() => {
    if (phase !== "running") return;
    const id = setInterval(() => setTime((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const start = () => {
    setQuestions(pickQuestions());
    setPhase("running"); setIdx(0); setCorrect(0); setTime(0);
    setPicked(null); reported.current = false;
  };

  const choose = (opt) => {
    if (picked != null) return;
    setPicked(opt);
    const isRight = opt === questions[idx].answer;
    const nextCorrect = correct + (isRight ? 1 : 0);
    if (isRight) setCorrect(nextCorrect);
    setTimeout(() => {
      if (idx + 1 >= questions.length) {
        setPhase("done");
      } else {
        setIdx(idx + 1);
        setPicked(null);
      }
    }, 600);
  };

  const iq = iqFor(correct);

  useEffect(() => {
    if (phase === "done" && !reported.current) {
      reported.current = true;
      onScore?.(correct * 100); // leaderboard score
    }
  }, [phase, correct, onScore]);

  if (phase === "idle") {
    return (
      <div className="text-center py-8" data-testid="iq-test-intro">
        <Brain className="w-12 h-12 text-neon-lime mx-auto mb-4" />
        <h3 className="font-display font-black text-2xl uppercase mb-2">Pattern IQ Test</h3>
        <p className="text-zinc-400 max-w-sm mx-auto mb-6">10 number-sequence puzzles. Find what comes next. We'll estimate your pattern IQ score!</p>
        <Button onClick={start} data-testid="iq-start" className="bg-neon-lime text-black rounded-none font-bold uppercase tracking-widest h-12 px-8">
          <Play className="w-4 h-4 mr-2 fill-black" /> Start Test
        </Button>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="text-center py-8" data-testid="iq-result">
        <p className="text-zinc-400 uppercase tracking-widest text-xs mb-2">Your estimated pattern IQ</p>
        <p className="font-display font-black text-7xl text-neon-lime text-glow">{iq}</p>
        <p className="font-display font-bold text-xl mt-2">{label(iq)}</p>
        <p className="text-zinc-400 mt-2">{correct}/{questions.length} correct · {time}s</p>
        <Button onClick={start} className="mt-6 bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest">
          <RotateCcw className="w-4 h-4 mr-2" /> Retake
        </Button>
      </div>
    );
  }

  const cur = questions[idx];
  return (
    <div className="w-full max-w-md mx-auto" data-testid="iq-test">
      <div className="flex items-center justify-between font-mono text-sm mb-4">
        <span className="text-neon-lime">Q {idx + 1}/{questions.length}</span>
        <span className="text-neon-yellow">⏱ {time}s</span>
      </div>
      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-6">
        <div className="h-full bg-neon-lime transition-all" style={{ width: `${(idx / questions.length) * 100}%` }} />
      </div>
      <div className="text-center py-8 rounded-xl border-2 border-zinc-700 bg-zinc-900 mb-6">
        <span className="font-display font-black text-4xl tracking-wide">{cur.q}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {cur.options.map((opt, i) => {
          const isAnswer = opt === cur.answer;
          const show = picked != null;
          const cls = show
            ? isAnswer ? "border-neon-lime bg-neon-lime/15"
              : opt === picked ? "border-neon-pink bg-neon-pink/15" : "border-zinc-800 opacity-50"
            : "border-zinc-700 hover:border-neon-lime";
          return (
            <button key={i} onClick={() => choose(opt)} data-testid={`iq-option-${i}`} disabled={picked != null}
              className={`py-5 rounded-lg bg-zinc-950 border-2 font-display font-bold text-2xl transition-colors ${cls}`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
